import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { EditOutlined as EditOutlinedIcon } from "../../../../../../components/icons";
import IconButton from "@mui/material/IconButton";
import Input from "../../../../../../components/Input";
import ButtonComp from "../../../../../../components/Button";
import Loader from "../../../../../../components/Loader";
import SignatureUploadField from "../../../../../../components/SignatureUploadField";
import VerifiedPopup from "../../../../../../components/VerifiedPopup";
import { useBodyRemovalsStore } from "../../../../../../store/useBodyRemovalsStore";
import { useAuthStore } from "../../../../../../store/useAuthStore";
import { useToast } from "../../../../../../hooks/useToast";
import { useRelationOptions } from "../../../../../../hooks/useRelationOptions";
import {
  downloadBodyRemovalDocument,
  getBodyRemovalDetails,
  updateBodyRemovalDetails,
} from "../../../../../../services/dashboard";
import {
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsSectionHeader,
} from "../../../../ifas/components/OperationsUI";

const BODY_SIZE_OPTIONS = ["Normal", "Oversized", "Extra Oversized"];
const PLACE_OF_DEATH_OPTIONS = [
  "Home",
  "Hospital",
  "Road Accident Scene",
  "Other",
];
const DECOMPOSITION_OPTIONS = [
  "None",
  "Mild",
  "Moderate",
  "Advanced",
];

const PERSISTED_BODY_REMOVAL_FIELDS = [
  "funeralFileNumber",
  "mortuaryName",
  "familyContactPerson",
  "familyContactNumbers",
  "relationToDeceased",
  "placeOfDeath",
  "deliveryMortuaryAddress",
  "nearestPoliceStation",
  "dateOfRemoval",
  "timeOfDeath",
  "bodySize",
  "stateOfDecomposition",
  "injuryDetected",
  "familySignature",
  "icbStaffSignature",
];

const readOnlyFieldBackground = "#F5F5F5";
const editableFieldBackground = "#FFFFFF";

const hasText = (value: any) => String(value ?? "").trim() !== "";
const sanitizeDisplayValue = (value: any) => {
  const normalized = String(value ?? "").trim();
  const lowered = normalized.toLowerCase();
  if (!normalized || lowered === "null" || lowered === "undefined") {
    return "";
  }
  return normalized;
};

const signatureStatusLabel = (value: any) =>
  hasText(value) ? "Uploaded" : "Not captured";

const isAfterTime = (later: string, earlier: string) =>
  Boolean(later && earlier && later > earlier);

const createInitialFormData = (source: any, capturedBy: string) => ({
  funeralFileNumber: source?.funeralFileNumber || "",
  mortuaryName: source?.mortuaryName || "",
  familyContactPerson: source?.familyContactPerson || "",
  familyContactNumbers: source?.familyContactNumbers || "",
  relationToDeceased: source?.relationToDeceased || "",
  placeOfDeath: source?.placeOfDeath || "",
  deliveryMortuaryAddress: source?.deliveryMortuaryAddress || "",
  nearestPoliceStation: source?.nearestPoliceStation || "",
  dateOfDeath: source?.dateOfDeath || "",
  timeOfDeath: source?.timeOfDeath || "",
  dateOfRemoval: source?.dateOfRemoval || "",
  bodySize: source?.bodySize || "",
  jewelleryRemovedWithDeceased:
    source?.jewelleryRemovedWithDeceased ?? false,
  jewelleryType: source?.jewelleryType || "",
  stateOfDecomposition: source?.stateOfDecomposition || "",
  clothesRemovedWithDeceased: source?.clothesRemovedWithDeceased ?? false,
  clothesType: source?.clothesType || "",
  bottomClothingDescription: source?.bottomClothingDescription || "",
  topClothingDescription: source?.topClothingDescription || "",
  underneathClothingDescription: source?.underneathClothingDescription || "",
  blanketsRemovedWithDeceased: source?.blanketsRemovedWithDeceased ?? false,
  blanketColor: source?.blanketColor || "",
  marksOrScars: source?.marksOrScars || "",
  injuryDetected:
    source?.injuryDetected === true
      ? true
      : source?.injuryDetected === false
        ? false
        : null,
  typeOfInjury: source?.typeOfInjury || "",
  hasIcebolethuPolicy: source?.hasIcebolethuPolicy ?? false,
  timeOutFromMortuary: source?.timeOutFromMortuary || "",
  kmOutFromMortuary: source?.kmOutFromMortuary ?? "",
  arrivalTimeToFamily: source?.arrivalTimeToFamily || "",
  departureTimeFromFamily: source?.departureTimeFromFamily || "",
  arrivalTimeInMortuary: source?.arrivalTimeInMortuary || "",
  kmInArrivalAtMortuary: source?.kmInArrivalAtMortuary ?? "",
  familySignature: source?.familySignature || "",
  familySignatureDate: source?.familySignatureDate || "",
  icbStaffName: source?.icbStaffName || "",
  icbStaffDesignation: source?.icbStaffDesignation || "",
  icbStaffSignature: source?.icbStaffSignature || "",
  icbStaffDate: source?.icbStaffDate || "",
  comments: source?.comments || "",
  capturedBy,
});

const normalizeTime = (value: string) => {
  if (!value) return "";
  if (value.length === 5) return `${value}:00`;
  return value;
};

const deceasedValue = (data: any, field: string, fallbackField?: string) =>
  data?.[field] || data?.[fallbackField || field] || "";

export default function BodyRemovalForm({
  deceasedData,
  canEditDetails = true,
}: any) {
  const { selectedBodyRemoval, setSelectedBodyRemoval } = useBodyRemovalsStore();
  const { first_name, last_name, user_name, userId } = useAuthStore();
  const { showInfoToast } = useToast();
  const { relationOptions, loading } = useRelationOptions();

  const safeFirstName = sanitizeDisplayValue(first_name);
  const safeLastName = sanitizeDisplayValue(last_name);
  const safeUsername = sanitizeDisplayValue(user_name);
  const displayName = [safeFirstName, safeLastName].filter(Boolean).join(" ");
  const sourceData = useMemo(
    () => selectedBodyRemoval?.bodyRemoval || selectedBodyRemoval,
    [selectedBodyRemoval],
  );
  const capturedBy =
    sourceData?.capturedBy ||
    sourceData?.updatedBy ||
    sourceData?.updated_by ||
    sourceData?.captured_by ||
    displayName ||
    safeUsername ||
    "";
  const [formData, setFormData] = useState(() =>
    createInitialFormData(selectedBodyRemoval, capturedBy),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [showVerified, setShowVerified] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const isBodyRemovalAdded = useMemo(
    () =>
      PERSISTED_BODY_REMOVAL_FIELDS.some((field) => {
        const value = sourceData?.[field];
        return value !== undefined && value !== null && String(value).trim() !== "";
      }),
    [sourceData],
  );
  const bodyRemovalStatus = String(
    sourceData?.bodyRemovalStatus || sourceData?.status || "",
  ).toUpperCase();
  const canCaptureKmIn = [
    "ARRIVED_AT_MORTUARY",
    "BODY_DELIVERED",
    "DELIVERED",
    "COMPLETED",
  ].includes(bodyRemovalStatus);
  const isReadOnly = !canEditDetails || (isBodyRemovalAdded && !isEditing);
  const hasMortuaryHandoverDetails = useMemo(
    () =>
      Boolean(
        sourceData?.mortuaryRegisterNumber ||
          sourceData?.mortuaryReceivedBy ||
          sourceData?.mortuaryReceivedAt ||
          sourceData?.mortuaryStorageLocation ||
          sourceData?.mortuaryRemarks ||
          sourceData?.bodyDeliveryNotes,
      ),
    [sourceData],
  );

  const releasePreviewUrl = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
  };

  const getBodyRemovalPdfUrl = async () => {
    if (!selectedBodyRemoval?.bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return null;
    }

    try {
      setIsDocumentLoading(true);
      const response = await downloadBodyRemovalDocument(
        selectedBodyRemoval.bodyRemovalNumber,
      );

      if (response?.status !== 200 || !response?.data) {
        showInfoToast(
          response?.data?.message || "Unable to generate body removal document",
        );
        return null;
      }

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/pdf" });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Body removal document generation failed", error);
      showInfoToast("Unable to generate body removal document");
      return null;
    } finally {
      setIsDocumentLoading(false);
    }
  };

  const handlePreviewDocument = async () => {
    const url = await getBodyRemovalPdfUrl();
    if (!url) return;
    releasePreviewUrl();
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleDownloadDocument = async () => {
    const url = await getBodyRemovalPdfUrl();
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedBodyRemoval?.bodyRemovalNumber || "body-removal"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handlePrintDocument = async () => {
    const url = await getBodyRemovalPdfUrl();
    if (!url) return;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    };

    document.body.appendChild(iframe);
  };

  const closePreviewDialog = () => {
    setPreviewOpen(false);
    releasePreviewUrl();
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
      ...(field === "jewelleryRemovedWithDeceased" && !checked
        ? { jewelleryType: "" }
        : {}),
      ...(field === "clothesRemovedWithDeceased" && !checked
        ? {
          clothesType: "",
          bottomClothingDescription: "",
          topClothingDescription: "",
          underneathClothingDescription: "",
        }
        : {}),
      ...(field === "blanketsRemovedWithDeceased" && !checked
        ? { blanketColor: "" }
        : {}),
      ...(field === "injuryDetected" && !checked
  ? { typeOfInjury: "" }
  : {}),
    }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];

      if (field === "jewelleryRemovedWithDeceased" && !checked) {
        delete updated.jewelleryType;
      }

      if (field === "clothesRemovedWithDeceased" && !checked) {
        delete updated.clothesType;
        delete updated.topClothingDescription;
        delete updated.bottomClothingDescription;
        delete updated.underneathClothingDescription;
      }

      if (field === "blanketsRemovedWithDeceased" && !checked) {
        delete updated.blanketColor;
      }

      if (field === "injuryDetected" && !checked) {
  delete updated.typeOfInjury;
}

      return updated;
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!hasText(formData.funeralFileNumber)) {
      newErrors.funeralFileNumber = "Funeral file number is required";
    }

    if (!hasText(formData.mortuaryName)) {
      newErrors.mortuaryName = "Mortuary name is required";
    }

    if (!hasText(formData.familyContactPerson)) {
      newErrors.familyContactPerson = "Family contact person is required";
    }

    if (!hasText(formData.familyContactNumbers)) {
      newErrors.familyContactNumbers = "Family contact numbers are required";
    }

    if (!formData.relationToDeceased) {
      newErrors.relationToDeceased = "Relation to deceased is required";
    }

    if (!formData.placeOfDeath) {
      newErrors.placeOfDeath = "Place of death is required";
    }

    if (!hasText(formData.deliveryMortuaryAddress)) {
      newErrors.deliveryMortuaryAddress =
        "Delivery mortuary address is required";
    }

    if (!hasText(formData.nearestPoliceStation)) {
      newErrors.nearestPoliceStation = "Nearest police station is required";
    }

    if (!formData.timeOfDeath) {
      newErrors.timeOfDeath = "Time of death is required";
    }

    if (!formData.dateOfRemoval) {
      newErrors.dateOfRemoval = "Date of removal is required";
    }

    if (!formData.bodySize) {
      newErrors.bodySize = "Body size is required";
    }

    if (!formData.stateOfDecomposition) {
      newErrors.stateOfDecomposition =
        "State of decomposition is required";
    }

    if (formData.jewelleryRemovedWithDeceased && !hasText(formData.jewelleryType)) {
      newErrors.jewelleryType = "Jewellery type is required";
    }

    if (formData.clothesRemovedWithDeceased) {
      if (!hasText(formData.clothesType)) {
        newErrors.clothesType = "Clothes type is required";
      }

      if (!hasText(formData.topClothingDescription)) {
        newErrors.topClothingDescription =
          "Top clothing description is required";
      }

      if (!hasText(formData.bottomClothingDescription)) {
        newErrors.bottomClothingDescription =
          "Bottom clothing description is required";
      }

      if (!hasText(formData.underneathClothingDescription)) {
        newErrors.underneathClothingDescription =
          "Clothing description is required";
      }
    }

    if (formData.blanketsRemovedWithDeceased && !hasText(formData.blanketColor)) {
      newErrors.blanketColor = "Blanket color is required";
    }

    if (formData.injuryDetected === null) {
      newErrors.injuryDetected = "Injury detected selection is required";
    }

  if (formData.injuryDetected === true && !hasText(formData.typeOfInjury)) {
  newErrors.typeOfInjury = "Type of injury is required";
}


    if (
      formData.arrivalTimeToFamily &&
      formData.timeOutFromMortuary &&
      !isAfterTime(formData.arrivalTimeToFamily, formData.timeOutFromMortuary)
    ) {
      newErrors.arrivalTimeToFamily =
        "Arrival time to family must be after time out from mortuary";
    }

    if (
      formData.departureTimeFromFamily &&
      formData.arrivalTimeToFamily &&
      !isAfterTime(
        formData.departureTimeFromFamily,
        formData.arrivalTimeToFamily,
      )
    ) {
      newErrors.departureTimeFromFamily =
        "Departure time from family must be after arrival time to family";
    }

    if (
      formData.arrivalTimeInMortuary &&
      formData.departureTimeFromFamily &&
      !isAfterTime(
        formData.arrivalTimeInMortuary,
        formData.departureTimeFromFamily,
      )
    ) {
      newErrors.arrivalTimeInMortuary =
        "Arrival time in mortuary must be after departure time from family";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = async () => {
    if (!canEditDetails) {
      return;
    }

    if (!selectedBodyRemoval?.bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    if (!validateForm()) {
      showInfoToast("Please complete the required body removal details");
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        ...formData,
        timeOfDeath: normalizeTime(formData.timeOfDeath),
        timeOutFromMortuary: normalizeTime(formData.timeOutFromMortuary),
        arrivalTimeToFamily: normalizeTime(formData.arrivalTimeToFamily),
        departureTimeFromFamily: normalizeTime(
          formData.departureTimeFromFamily,
        ),
        arrivalTimeInMortuary: normalizeTime(formData.arrivalTimeInMortuary),
        kmOutFromMortuary:
          formData.kmOutFromMortuary === ""
            ? null
            : Number(formData.kmOutFromMortuary),
        kmInArrivalAtMortuary:
          formData.kmInArrivalAtMortuary === ""
            ? null
            : Number(formData.kmInArrivalAtMortuary),
        updatedBy: capturedBy,
      };

      const response = await updateBodyRemovalDetails(
        selectedBodyRemoval.bodyRemovalNumber,
        payload,
      );

      if (response?.status === 200 || response?.status === 201) {
        const refreshed = await getBodyRemovalDetails(
          selectedBodyRemoval.bodyRemovalNumber,
        );

        if (refreshed?.status === 200) {
          setSelectedBodyRemoval(refreshed.data);
          setFormData(createInitialFormData(refreshed.data, capturedBy));
        }
        setIsEditing(false);
        setShowVerified(true);
      } else {
        showInfoToast(
          response?.data?.message || "Unable to save body removal details",
        );
      }
    } catch (error) {
      console.error("Body removal update failed", error);
      showInfoToast("Unable to save body removal details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setFormData(createInitialFormData(sourceData, capturedBy));
  }, [sourceData, capturedBy]);

  useEffect(() => {
    if (!showVerified) {
      return;
    }

    const timer = setTimeout(() => {
      setShowVerified(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [showVerified]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (isBodyRemovalAdded && !isEditing) {
    const yesNo = (value: boolean) => (value ? "Yes" : "No");

    return (
      <Box>
        {(isLoading || isDocumentLoading) && (
          <Loader isLoading={isLoading || isDocumentLoading} />
        )}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={1.5}
          mb={2}
          flexWrap="wrap"
        >
          <ButtonComp
            variant="outlined"
            height={38}
            width={120}
            textTransform="none"
            onClick={handlePreviewDocument}
            disabled={isDocumentLoading}
          >
            Preview
          </ButtonComp>
          <ButtonComp
            variant="outlined"
            height={38}
            width={120}
            textTransform="none"
            onClick={handlePrintDocument}
            disabled={isDocumentLoading}
          >
            Print
          </ButtonComp>
          <ButtonComp
            variant="contained"
            height={38}
            width={120}
            textTransform="none"
            onClick={handleDownloadDocument}
            disabled={isDocumentLoading}
          >
            Download
          </ButtonComp>
        </Box>
        {deceasedData ? (
          <>
            <OperationsSectionHeader title="Deceased Details" />
            <OperationsDetailGrid>
              <OperationsDetailField
                label="Title"
                value={deceasedValue(deceasedData, "title")}
              />
              <OperationsDetailField
                label="Initials"
                value={deceasedValue(deceasedData, "initials")}
              />
              <OperationsDetailField
                label="First Name"
                value={deceasedValue(deceasedData, "firstName", "first_name")}
              />
              <OperationsDetailField
                label="Last Name"
                value={deceasedValue(deceasedData, "lastName", "last_name")}
              />
              <OperationsDetailField
                label="Identity Type"
                value={deceasedValue(
                  deceasedData,
                  "identificationType",
                  "identification_type",
                )}
              />
              <OperationsDetailField
                label="Identity Number"
                value={deceasedValue(
                  deceasedData,
                  "identificationNumber",
                  "identification_number",
                )}
              />
              <OperationsDetailField
                label="Gender"
                value={deceasedValue(deceasedData, "gender")}
              />
              <OperationsDetailField
                label="Date Of Birth"
                value={deceasedValue(
                  deceasedData,
                  "dateOfBirth",
                  "date_of_birth",
                )}
              />
              <OperationsDetailField
                label="Date Of Death"
                value={deceasedValue(
                  deceasedData,
                  "dateOfDeath",
                  "date_of_death",
                )}
              />
              <OperationsDetailField
                label="Cause Of Death"
                value={deceasedValue(
                  deceasedData,
                  "causeOfDeath",
                  "cause_of_death",
                )}
              />
              <OperationsDetailField
                label="Relationship To Informant"
                value={deceasedValue(deceasedData, "relationship")}
              />
            </OperationsDetailGrid>
          </>
        ) : null}

        <Box mt={2.5}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <OperationsSectionHeader title="Body Removal Details" />

            {!isEditing && (
              <IconButton onClick={() => setIsEditing(true)}>
                <EditOutlinedIcon />
              </IconButton>
            )}
          </Box>          <OperationsDetailGrid>
            <OperationsDetailField
              label="Funeral File Number"
              value={formData.funeralFileNumber}
            />
            <OperationsDetailField
              label="Mortuary Name"
              value={formData.mortuaryName}
            />
            <OperationsDetailField
              label="Family Contact Person"
              value={formData.familyContactPerson}
            />
            <OperationsDetailField
              label="Family Contact Number"
              value={formData.familyContactNumbers}
            />
            <OperationsDetailField
              label="Relation To Deceased"
              value={formData.relationToDeceased}
            />
            <OperationsDetailField
              label="Place Of Death"
              value={formData.placeOfDeath}
            />
            <OperationsDetailField
              label="Delivery Mortuary Address"
              value={formData.deliveryMortuaryAddress}
              span={2}
            />
            <OperationsDetailField
              label="Nearest Police Station"
              value={formData.nearestPoliceStation}
            />
            <OperationsDetailField
              label="Time Of Death"
              value={formData.timeOfDeath}
            />
            <OperationsDetailField
              label="Date Of Removal"
              value={formData.dateOfRemoval}
            />
            <OperationsDetailField
              label="Body Size"
              value={formData.bodySize}
            />
            <OperationsDetailField
              label="State Of Decomposition"
              value={formData.stateOfDecomposition}
            />
          </OperationsDetailGrid>
        </Box>

        <Box mt={2.5}>
          <OperationsSectionHeader title="Removed With Deceased" />
          <OperationsDetailGrid>
            <OperationsDetailField
              label="Jewellery Removed"
              value={yesNo(formData.jewelleryRemovedWithDeceased)}
            />
            <OperationsDetailField
              label="Jewellery Type"
              value={formData.jewelleryType}
            />
            <OperationsDetailField
              label="Clothes Removed"
              value={yesNo(formData.clothesRemovedWithDeceased)}
            />
            <OperationsDetailField
              label="Clothes Type"
              value={formData.clothesType}
            />
            <OperationsDetailField
              label="Top Clothing"
              value={formData.topClothingDescription}
            />
            <OperationsDetailField
              label="Bottom Clothing"
              value={formData.bottomClothingDescription}
            />
            <OperationsDetailField
              label="Underneath Clothing"
              value={formData.underneathClothingDescription}
            />
            <OperationsDetailField
              label="Blankets Removed"
              value={yesNo(formData.blanketsRemovedWithDeceased)}
            />
            <OperationsDetailField
              label="Blanket Color"
              value={formData.blanketColor}
            />
            <OperationsDetailField
              label="Marks Or Scars"
              value={formData.marksOrScars}
            />
            <OperationsDetailField
              label="Has Icebolethu Policy"
              value={yesNo(formData.hasIcebolethuPolicy)}
            />
            <OperationsDetailField
              label="Injury Detected"
              value={
                formData.injuryDetected === null
                  ? ""
                  : yesNo(Boolean(formData.injuryDetected))
              }
            />
            <OperationsDetailField
  label="Type of Injury"
  value={formData.typeOfInjury}
/>
          </OperationsDetailGrid>
        </Box>

        <Box mt={2.5}>
          <OperationsSectionHeader title="Movement Details" />
          <OperationsDetailGrid>
            <OperationsDetailField
              label="Time Out From Mortuary"
              value={formData.timeOutFromMortuary}
            />
            <OperationsDetailField
              label="KM Out From Mortuary"
              value={formData.kmOutFromMortuary}
            />
            <OperationsDetailField
              label="Arrival Time To Family"
              value={formData.arrivalTimeToFamily}
            />
            <OperationsDetailField
              label="Departure Time From Family"
              value={formData.departureTimeFromFamily}
            />
            <OperationsDetailField
              label="Arrival Time In Mortuary"
              value={formData.arrivalTimeInMortuary}
            />
            <OperationsDetailField
              label="KM In Arrival At Mortuary"
              value={formData.kmInArrivalAtMortuary}
            />
            <OperationsDetailField label="Captured By" value={capturedBy} />
            <OperationsDetailField
                label="Family Signature"
                value={signatureStatusLabel(formData.familySignature)}
              />
              <OperationsDetailField
                label="Family Signature Date"
                value={formData.familySignatureDate}
              />
              <OperationsDetailField
                label="ICB Staff Name"
                value={formData.icbStaffName}
              />
              <OperationsDetailField
                label="ICB Staff Designation"
                value={formData.icbStaffDesignation}
              />
              <OperationsDetailField
                label="ICB Staff Signature"
                value={signatureStatusLabel(formData.icbStaffSignature)}
              />
              <OperationsDetailField
                label="ICB Staff Date"
                value={formData.icbStaffDate}
              />
              <OperationsDetailField
                label="Comments"
                value={formData.comments}
              span={3}
            />
          </OperationsDetailGrid>
        </Box>

        {hasMortuaryHandoverDetails && (
          <Box mt={2.5}>
            <OperationsSectionHeader title="Mortuary Handover" />
            <OperationsDetailGrid>
              <OperationsDetailField
                label="Register Number"
                value={sourceData?.mortuaryRegisterNumber}
              />
              <OperationsDetailField
                label="Received By"
                value={sourceData?.mortuaryReceivedBy}
              />
              <OperationsDetailField
                label="Received At"
                value={sourceData?.mortuaryReceivedAt}
              />
              <OperationsDetailField
                label="Storage Location"
                value={sourceData?.mortuaryStorageLocation}
              />
              <OperationsDetailField
                label="Delivery Notes"
                value={sourceData?.bodyDeliveryNotes}
                span={2}
              />
              <OperationsDetailField
                label="Mortuary Remarks"
                value={sourceData?.mortuaryRemarks}
                span={2}
              />
            </OperationsDetailGrid>
          </Box>
        )}

        <Dialog
          open={previewOpen}
          onClose={closePreviewDialog}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>Body Removal Document Preview</DialogTitle>
          <DialogContent dividers>
            {previewUrl ? (
              <object
                data={previewUrl}
                type="application/pdf"
                width="100%"
                height="720px"
              >
                <Typography>
                  PDF preview is not supported in this browser.{" "}
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                    Open in new tab
                  </a>
                </Typography>
              </object>
            ) : null}
          </DialogContent>
          <DialogActions>
            <ButtonComp
              variant="outlined"
              height={40}
              width={120}
              textTransform="none"
              onClick={closePreviewDialog}
            >
              Close
            </ButtonComp>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      {(isLoading || isDocumentLoading) && (
        <Loader isLoading={isLoading || isDocumentLoading} />
      )}
      <Box display="flex" justifyContent="flex-end" gap={1.5} mb={2} flexWrap="wrap">
        <ButtonComp
          variant="outlined"
          height={38}
          width={120}
          textTransform="none"
          onClick={handlePreviewDocument}
          disabled={isDocumentLoading || isEditing || !isBodyRemovalAdded}
        >
          Preview
        </ButtonComp>
        <ButtonComp
          variant="outlined"
          height={38}
          width={120}
          textTransform="none"
          onClick={handlePrintDocument}
          disabled={isDocumentLoading || isEditing || !isBodyRemovalAdded}
        >
          Print
        </ButtonComp>
        <ButtonComp
          variant="contained"
          height={38}
          width={120}
          textTransform="none"
          onClick={handleDownloadDocument}
          disabled={isDocumentLoading || isEditing || !isBodyRemovalAdded}
        >
          Download
        </ButtonComp>
      </Box>
      <Grid
        container
        rowSpacing={3}
        columnSpacing={3}
        mt={1}
        textAlign="left"
        p={2}
      >
        <Grid size={{ xs: 12 }}>
          <OperationsSectionHeader title="Deceased Details" />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <OperationsDetailGrid>
            <OperationsDetailField label="Title" value={deceasedValue(deceasedData, "title")} />
            <OperationsDetailField label="Initials" value={deceasedValue(deceasedData, "initials")} />
            <OperationsDetailField label="First Name" value={deceasedValue(deceasedData, "firstName", "first_name")} />
            <OperationsDetailField label="Last Name" value={deceasedValue(deceasedData, "lastName", "last_name")} />
            <OperationsDetailField label="Identity Type" value={deceasedValue(deceasedData, "identificationType", "identification_type")} />
            <OperationsDetailField label="Identity Number" value={deceasedValue(deceasedData, "identificationNumber", "identification_number")} />
            <OperationsDetailField label="Gender" value={deceasedValue(deceasedData, "gender")} />
            <OperationsDetailField label="Date Of Birth" value={deceasedValue(deceasedData, "dateOfBirth", "date_of_birth")} />
            <OperationsDetailField label="Date Of Death" value={deceasedValue(deceasedData, "dateOfDeath", "date_of_death")} />
            <OperationsDetailField label="Cause Of Death" value={deceasedValue(deceasedData, "causeOfDeath", "cause_of_death")} />
            <OperationsDetailField label="Relationship To Informant" value={deceasedValue(deceasedData, "relationship")} />
          </OperationsDetailGrid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <OperationsSectionHeader title="Body Removal Details" />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={formData.funeralFileNumber}
            onChange={() => undefined}
            height={40}
            width="100%"
            label="Funeral File Number"
            backgroundColor={readOnlyFieldBackground}
            helperText="Auto-generated from the case and shared across all related operations."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={formData.mortuaryName}
            onChange={() => undefined}
            height={40}
            width="100%"
            label="Mortuary Name"
            backgroundColor={readOnlyFieldBackground}
            error={Boolean(errors.mortuaryName)}
            helperText={errors.mortuaryName || "Selected in Body Location and shared with the Mortuary module."}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={isReadOnly}
            value={formData.familyContactPerson}
            onChange={(e: any) =>
              updateField("familyContactPerson", e.target.value)
            }
            height={40}
            width="100%"
            label="Family Contact Person"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.familyContactPerson)}
            helperText={errors.familyContactPerson}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={isReadOnly}
            value={formData.familyContactNumbers}
            onChange={(e: any) =>
              updateField("familyContactNumbers", e.target.value)
            }
            height={40}
            width="100%"
            label="Family Contact Number"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.familyContactNumbers)}
            helperText={errors.familyContactNumbers}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            disabled={isReadOnly}
            value={formData.relationToDeceased}
            onChange={(e: any) =>
              updateField("relationToDeceased", e.target.value)
            }
            height={40}
            width="100%"
            label="Relation To Deceased"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.relationToDeceased)}
            helperText={errors.relationToDeceased}
          >
            {loading ? (
              <MenuItem disabled>Loading relationships...</MenuItem>
            ) : (
              relationOptions.map((option: string) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))
            )}
          </Input>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            disabled={isReadOnly} value={formData.placeOfDeath}
            onChange={(e: any) => updateField("placeOfDeath", e.target.value)}
            height={40}
            width="100%"
            label="Place Of Death"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.placeOfDeath)}
            helperText={errors.placeOfDeath}
          >
            {PLACE_OF_DEATH_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Input>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={isReadOnly} value={formData.deliveryMortuaryAddress}
            onChange={(e: any) =>
              updateField("deliveryMortuaryAddress", e.target.value)
            }
            height={40}
            width="100%"
            label="Delivery Mortuary Address"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.deliveryMortuaryAddress)}
            helperText={errors.deliveryMortuaryAddress}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={isReadOnly} value={formData.nearestPoliceStation}
            onChange={(e: any) =>
              updateField("nearestPoliceStation", e.target.value)
            }
            height={40}
            width="100%"
            label="Nearest Police Station"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.nearestPoliceStation)}
            helperText={errors.nearestPoliceStation}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="time"
            disabled={isReadOnly} value={formData.timeOfDeath}
            onChange={(e: any) => updateField("timeOfDeath", e.target.value)}
            height={40}
            width="100%"
            label="Time Of Death"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.timeOfDeath)}
            helperText={errors.timeOfDeath}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="date"
            disabled={isReadOnly}
            value={formData.dateOfRemoval}
            onChange={(e: any) => updateField("dateOfRemoval", e.target.value)}
            height={40}
            width="100%"
            label="Date Of Removal"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.dateOfRemoval)}
            helperText={errors.dateOfRemoval}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            disabled={isReadOnly} value={formData.bodySize}
            onChange={(e: any) => updateField("bodySize", e.target.value)}
            height={40}
            width="100%"
            label="Body Size"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.bodySize)}
            helperText={errors.bodySize}
          >
            {BODY_SIZE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            disabled={isReadOnly} value={formData.stateOfDecomposition}
            onChange={(e: any) =>
              updateField("stateOfDecomposition", e.target.value)
            }
            height={40}
            width="100%"
            label="State Of Decomposition"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.stateOfDecomposition)}
            helperText={errors.stateOfDecomposition}
          >
            {DECOMPOSITION_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            disabled={isReadOnly}
            value={
              formData.injuryDetected === null
                ? ""
                : String(formData.injuryDetected)
            }
            onChange={(e: any) =>
              updateField(
                "injuryDetected",
                e.target.value === ""
                  ? null
                  : e.target.value === "true",
              )
            }
            height={40}
            width="100%"
            label="Injury Detected"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.injuryDetected)}
            helperText={errors.injuryDetected}
          >
            <MenuItem value="">Select</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Input>
        </Grid>
       <Grid size={{ xs: 12, md: 4 }}>
  <Input
    select
    disabled={isReadOnly || formData.injuryDetected !== true}
    value={formData.typeOfInjury}
    onChange={(e: any) => updateField("typeOfInjury", e.target.value)}
    height={40}
    width="100%"
    label="Type of Injury"
    backgroundColor={
      isReadOnly
        ? readOnlyFieldBackground
        : formData.injuryDetected === true
          ? editableFieldBackground
          : readOnlyFieldBackground
    }
    error={Boolean(errors.typeOfInjury)}
    helperText={errors.typeOfInjury}
  >
    <MenuItem value="">Select</MenuItem>
    <MenuItem value="Minor">Minor</MenuItem>
    <MenuItem value="Major">Major</MenuItem>
  </Input>
</Grid>

        <Grid size={{ xs: 12 }}>
          <OperationsSectionHeader title="Removed With Deceased" />

          <Box
            sx={{
              border: "1px solid #E6E6E6",
              borderRadius: "10px",
              px: 2,
              py: 1.5,
              backgroundColor: "#FAFAFA",
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isReadOnly} checked={formData.jewelleryRemovedWithDeceased}
                  onChange={(e) =>
                    handleCheckboxChange(
                      "jewelleryRemovedWithDeceased",
                      e.target.checked,
                    )
                  }
                />
              }
              label="Jewellery"
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isReadOnly} checked={formData.clothesRemovedWithDeceased}
                  onChange={(e) =>
                    handleCheckboxChange(
                      "clothesRemovedWithDeceased",
                      e.target.checked,
                    )
                  }
                />
              }
              label="Clothes"
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isReadOnly} checked={formData.blanketsRemovedWithDeceased}
                  onChange={(e) =>
                    handleCheckboxChange(
                      "blanketsRemovedWithDeceased",
                      e.target.checked,
                    )
                  }
                />
              }
              label="Blankets"
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={isReadOnly} checked={formData.hasIcebolethuPolicy}
                  onChange={(e) =>
                    updateField("hasIcebolethuPolicy", e.target.checked)
                  }
                />
              }
              label="Has Icebolethu Policy"
              sx={{ m: 0 }}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={
              isReadOnly || !formData.jewelleryRemovedWithDeceased
            }
            value={formData.jewelleryType}
            onChange={(e: any) => updateField("jewelleryType", e.target.value)}
            height={40}
            width="100%"
            label="Jewellery Type"
            backgroundColor={
              isReadOnly
                ? readOnlyFieldBackground
                : formData.jewelleryRemovedWithDeceased
                  ? editableFieldBackground
                  : readOnlyFieldBackground
            }
            error={Boolean(errors.jewelleryType)}
            helperText={errors.jewelleryType}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={
              isReadOnly || !formData.clothesRemovedWithDeceased
            }
            value={formData.clothesType}
            onChange={(e: any) => updateField("clothesType", e.target.value)}
            height={40}
            width="100%"
            label="Clothes Type"
            backgroundColor={
              isReadOnly
                ? readOnlyFieldBackground
                : formData.clothesRemovedWithDeceased
                  ? editableFieldBackground
                  : readOnlyFieldBackground
            }
            error={Boolean(errors.clothesType)}
            helperText={errors.clothesType}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={
              isReadOnly || !formData.clothesRemovedWithDeceased
            }
            value={formData.topClothingDescription}
            onChange={(e: any) =>
              updateField("topClothingDescription", e.target.value)
            }
            height={40}
            width="100%"
            label="Top Clothing Description"
            backgroundColor={
              isReadOnly
                ? readOnlyFieldBackground
                : formData.clothesRemovedWithDeceased
                  ? editableFieldBackground
                  : readOnlyFieldBackground
            }
            error={Boolean(errors.topClothingDescription)}
            helperText={errors.topClothingDescription}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={
              isReadOnly || !formData.clothesRemovedWithDeceased
            }
            value={formData.bottomClothingDescription}
            onChange={(e: any) =>
              updateField("bottomClothingDescription", e.target.value)
            }
            height={40}
            width="100%"
            label="Bottom Clothing Description"
            backgroundColor={
              isReadOnly
                ? readOnlyFieldBackground
                : formData.clothesRemovedWithDeceased
                  ? editableFieldBackground
                  : readOnlyFieldBackground
            }
            error={Boolean(errors.bottomClothingDescription)}
            helperText={errors.bottomClothingDescription}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={
              isReadOnly || !formData.clothesRemovedWithDeceased
            }
            value={formData.underneathClothingDescription}
            onChange={(e: any) =>
              updateField("underneathClothingDescription", e.target.value)
            }
            height={40}
            width="100%"
            label="Underneath Clothing"
            backgroundColor={
              isReadOnly
                ? readOnlyFieldBackground
                : formData.clothesRemovedWithDeceased
                  ? editableFieldBackground
                  : readOnlyFieldBackground
            }
            error={Boolean(errors.underneathClothingDescription)}
            helperText={errors.underneathClothingDescription}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={
              isReadOnly || !formData.blanketsRemovedWithDeceased
            }
            value={formData.blanketColor}
            onChange={(e: any) => updateField("blanketColor", e.target.value)}
            height={40}
            width="100%"
            label="Blanket Color"
            backgroundColor={
              isReadOnly
                ? readOnlyFieldBackground
                : formData.blanketsRemovedWithDeceased
                  ? editableFieldBackground
                  : readOnlyFieldBackground
            }
            error={Boolean(errors.blanketColor)}
            helperText={errors.blanketColor}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled={isReadOnly} value={formData.marksOrScars}
            onChange={(e: any) => updateField("marksOrScars", e.target.value)}
            height={40}
            width="100%"
            label="Marks Or Scars"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="time"
            disabled value={formData.timeOutFromMortuary}
            onChange={(e: any) =>
              updateField("timeOutFromMortuary", e.target.value)
            }
            height={40}
            width="100%"
            label="Time Out From Mortuary"
            backgroundColor={readOnlyFieldBackground}
            error={Boolean(errors.timeOutFromMortuary)}
            helperText={
              errors.timeOutFromMortuary ||
              "Captured automatically when the driver marks En Route"
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="number"
            disabled={isReadOnly} value={formData.kmOutFromMortuary}
            onChange={(e: any) =>
              updateField("kmOutFromMortuary", e.target.value)
            }
            height={40}
            width="100%"
            label="KM Out From Mortuary"
            backgroundColor={
              isReadOnly ? readOnlyFieldBackground : editableFieldBackground
            }
            error={Boolean(errors.kmOutFromMortuary)}
            helperText={errors.kmOutFromMortuary}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="time"
            disabled value={formData.arrivalTimeToFamily}
            onChange={(e: any) =>
              updateField("arrivalTimeToFamily", e.target.value)
            }
            height={40}
            width="100%"
            label="Arrival Time To Family"
            backgroundColor={readOnlyFieldBackground}
            error={Boolean(errors.arrivalTimeToFamily)}
            helperText={
              errors.arrivalTimeToFamily ||
              "Captured automatically when the driver arrives at scene"
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="time"
            disabled value={formData.departureTimeFromFamily}
            onChange={(e: any) =>
              updateField("departureTimeFromFamily", e.target.value)
            }
            height={40}
            width="100%"
            label="Departure Time From Family"
            backgroundColor={readOnlyFieldBackground}
            error={Boolean(errors.departureTimeFromFamily)}
            helperText={
              errors.departureTimeFromFamily ||
              "Captured automatically when body collection is confirmed"
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="time"
            disabled value={formData.arrivalTimeInMortuary}
            onChange={(e: any) =>
              updateField("arrivalTimeInMortuary", e.target.value)
            }
            height={40}
            width="100%"
            label="Arrival Time In Mortuary"
            backgroundColor={readOnlyFieldBackground}
            error={Boolean(errors.arrivalTimeInMortuary)}
            helperText={
              errors.arrivalTimeInMortuary ||
              "Captured automatically when the driver arrives at mortuary"
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="number"
            disabled={isReadOnly || !canCaptureKmIn} value={formData.kmInArrivalAtMortuary}
            onChange={(e: any) =>
              updateField("kmInArrivalAtMortuary", e.target.value)
            }
            height={40}
            width="100%"
            label="KM In Arrival At Mortuary"
            backgroundColor={
              isReadOnly || !canCaptureKmIn
                ? readOnlyFieldBackground
                : editableFieldBackground
            }
            error={Boolean(errors.kmInArrivalAtMortuary)}
            helperText={
              errors.kmInArrivalAtMortuary ||
              (!canCaptureKmIn ? "Captured when the driver arrives back at mortuary" : "")
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={capturedBy}
            height={40}
            width="100%"
            label="Captured By"
            backgroundColor="#F5F5F5"
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <OperationsSectionHeader title="Sign-Off Details" />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "18px",
              background: "linear-gradient(180deg, #ffffff 0%, #fcfcfd 100%)",
              p: 2.25,
              display: "grid",
              gap: 1.75,
              minWidth: 0,
              overflow: "hidden",
              boxShadow: "0 12px 28px rgba(15,23,42,0.04)",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                Family Sign-Off
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mt: 0.35 }}>
                Attach the family signature file and record the sign-off date.
              </Typography>
            </Box>

            <SignatureUploadField
              label="Family Signature"
              module="BODY_REMOVAL"
              referenceNumber={selectedBodyRemoval?.bodyRemovalNumber}
              fieldName="familySignature"
              value={formData.familySignature}
              disabled={isReadOnly}
              onUploaded={(fileKey) => updateField("familySignature", fileKey)}
            />

            <Input
              type="date"
              disabled={isReadOnly}
              value={formData.familySignatureDate}
              onChange={(e: any) => updateField("familySignatureDate", e.target.value)}
              height={40}
              width="100%"
              label="Family Signature Date"
              backgroundColor={
                isReadOnly ? readOnlyFieldBackground : editableFieldBackground
              }
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "18px",
              background: "linear-gradient(180deg, #ffffff 0%, #fcfcfd 100%)",
              p: 2.25,
              display: "grid",
              gap: 1.75,
              minWidth: 0,
              overflow: "hidden",
              boxShadow: "0 12px 28px rgba(15,23,42,0.04)",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                ICB Staff Sign-Off
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mt: 0.35 }}>
                Capture the staff identity details and attach the signed confirmation.
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ minWidth: 0 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  disabled={isReadOnly}
                  value={formData.icbStaffName}
                  onChange={(e: any) => updateField("icbStaffName", e.target.value)}
                  height={40}
                  width="100%"
                  label="ICB Staff Name"
                  backgroundColor={
                    isReadOnly ? readOnlyFieldBackground : editableFieldBackground
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  disabled={isReadOnly}
                  value={formData.icbStaffDesignation}
                  onChange={(e: any) => updateField("icbStaffDesignation", e.target.value)}
                  height={40}
                  width="100%"
                  label="ICB Staff Designation"
                  backgroundColor={
                    isReadOnly ? readOnlyFieldBackground : editableFieldBackground
                  }
                />
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ minWidth: 0 }}>
                <SignatureUploadField
                  label="ICB Staff Signature"
                  module="BODY_REMOVAL"
                  referenceNumber={selectedBodyRemoval?.bodyRemovalNumber}
                  fieldName="icbStaffSignature"
                  value={formData.icbStaffSignature}
                  disabled={isReadOnly}
                  onUploaded={(fileKey) => updateField("icbStaffSignature", fileKey)}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  type="date"
                  disabled={isReadOnly}
                  value={formData.icbStaffDate}
                  onChange={(e: any) => updateField("icbStaffDate", e.target.value)}
                  height={40}
                  width="100%"
                  label="ICB Staff Date"
                  backgroundColor={
                    isReadOnly ? readOnlyFieldBackground : editableFieldBackground
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <OperationsSectionHeader title="Comments" />

          <TextField
            disabled={isReadOnly} multiline
            rows={4}
            fullWidth
            value={formData.comments}
            onChange={(e: any) => updateField("comments", e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: isReadOnly
                  ? readOnlyFieldBackground
                  : editableFieldBackground,
              },
            }}
          />
        </Grid>

        {canEditDetails && (!isBodyRemovalAdded || isEditing) && (
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end">
              <ButtonComp
                variant="contained"
                height={40}
                width={190}
                textTransform="none"
                onClick={handleAdd}
              >
                {isEditing ? "Update" : "Add"}
              </ButtonComp>
            </Box>
          </Grid>
        )}
      </Grid>

      <VerifiedPopup
        label="Added"
        open={showVerified}
        onClose={() => setShowVerified(false)}
      />
      <Dialog
        open={previewOpen}
        onClose={closePreviewDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Body Removal Document Preview</DialogTitle>
        <DialogContent dividers>
          {previewUrl ? (
            <object
              data={previewUrl}
              type="application/pdf"
              width="100%"
              height="720px"
            >
              <Typography>
                PDF preview is not supported in this browser.{" "}
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  Open in new tab
                </a>
              </Typography>
            </object>
          ) : null}
        </DialogContent>
        <DialogActions>
          <ButtonComp
            variant="outlined"
            height={40}
            width={120}
            textTransform="none"
            onClick={closePreviewDialog}
          >
            Close
          </ButtonComp>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
